import { Key, useState } from 'react'
import Styles from './ModifiedActionBar.module.scss'
import { useRetryMessage } from 'hooks/queries/chat/useRetryMessage'
import { MoreDots, EyeOpen, Download } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ArrowRotateLeft, ChevronLeft, ChevronRight } from 'blaze-ui/icons/24'
import { ChatComparisonPayload } from '../ChatComparisonContext'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Menu } from 'components/Menu/Menu'
import { Item } from 'components/Menu/MenuItem'
import { useModals } from 'almanac-ui/Modal'
import { MediaPreviewModal } from 'components/ChatAssistant/Messages/Message/Content/GeneratedMediaContent/MediaPreviewModal'
import { ChatGeneratedImageContent, ChatGeneratedMediaContent, ChatGeneratedVideoContent } from 'api/clients/ChatClient'
import { fetchFile } from 'utils/fileDownloadUtils'
import { downloadFile } from 'components/VisualStudioPlayground/VisualEditor/utils/export/downloadFile'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

interface ModifiedActionBarProps {
  comparison: ChatComparisonPayload
  currentIndex: number
  totalCount: number
  onPrevious: () => void
  onNext: () => void
}

export const ModifiedActionBar = ({
  comparison,
  currentIndex,
  totalCount,
  onPrevious,
  onNext,
}: ModifiedActionBarProps) => {
  const { mutate: retryMessage, isLoading: isRetryLoading } = useRetryMessage()
  const { openModal } = useModals()
  const [isDownloading, setIsDownloading] = useState(false)

  const handleRetry = () => {
    retryMessage({ threadId: comparison.threadId, id: comparison.messageId })
  }

  const handlePreview = () => {
    const content: ChatGeneratedMediaContent =
      comparison.mediaType === 'video'
        ? ({
            type: 'generated_video',
            video_url: comparison.videoUrl ?? comparison.previewUrl,
            thumbnail_url: comparison.previewUrl,
            media_file_id: comparison.mediaFileId,
            design_id: comparison.designId,
          } as ChatGeneratedVideoContent)
        : ({
            type: 'generated_image',
            image_url: comparison.previewUrl,
            media_file_id: comparison.mediaFileId,
            design_id: comparison.designId,
            slide_index: comparison.slideIndex,
          } as ChatGeneratedImageContent)
    openModal(MediaPreviewModal, { content }, { newStack: true })
  }

  const handleDownload = async () => {
    setIsDownloading(true)
    const dismissToast = showToastMessage('Downloading...', 'loading')

    try {
      const downloadUrl = comparison.videoUrl ?? comparison.previewUrl
      const file = await fetchFile({ url: downloadUrl })
      downloadFile(file)
      showToastMessage('Download complete!')
    } catch {
      showToastMessage('Unable to download', 'error')
    } finally {
      setIsDownloading(false)
      dismissToast()
    }
  }

  const handleMenuAction = (key: Key) => {
    switch (key) {
      case 'preview':
        handlePreview()
        break
      case 'download':
        handleDownload()
        break
    }
  }

  return (
    <div className={Styles.root}>
      <div className={Styles.left}>
        <Text variant="secondary" color="var(--dark-90)">
          Modified
        </Text>
        <div className={Styles.pagination}>
          <IconButton
            variant="ghost"
            aria-label="Previous variation"
            onPress={onPrevious}
            icon={ChevronLeft}
            isDisabled={currentIndex <= 0}
            color="var(--dark-40)"
          />
          <Text variant="metadata" color="var(--dark-90)">
            {currentIndex + 1}/{totalCount}
          </Text>
          <IconButton
            variant="ghost"
            aria-label="Next variation"
            onPress={onNext}
            icon={ChevronRight}
            isDisabled={currentIndex >= totalCount - 1}
            color="var(--dark-40)"
          />
        </div>
      </div>
      <div className={Styles.right}>
        <Tooltip label="Retry">
          <IconButton
            variant="ghost"
            aria-label="Retry"
            onPress={handleRetry}
            icon={ArrowRotateLeft}
            isDisabled={isRetryLoading}
            color="var(--dark-40)"
          />
        </Tooltip>
        <DropdownTrigger placement="bottom-end">
          <IconButton
            aria-label="More actions"
            icon={MoreDots}
            variant="ghost"
            isDisabled={isDownloading}
            color="var(--dark-40)"
          />
          {() => (
            <Menu onAction={handleMenuAction} selectedKeys={[]}>
              <Item key="preview" textValue="Preview" frontIcon={EyeOpen} aria-label="Preview">
                Preview
              </Item>
              <Item key="download" textValue="Download" frontIcon={Download} aria-label="Download">
                Download
              </Item>
            </Menu>
          )}
        </DropdownTrigger>
      </div>
    </div>
  )
}
